<template>
    <app-layout>
        <template #title>Users</template>
        <template #description>Manage platform users</template>
        <template #actions>
            <inertia-link
                :href="route('app.kiosk.users.create')"
                class="gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-iron-900 text-white hover:bg-iron-800 inline-flex items-center transition-colors"
            >
                <Plus class="h-3 w-3" />
                Add user
            </inertia-link>
        </template>

        <KioskNav />

        <div class="max-w-7xl px-4 py-8 sm:px-6 lg:px-8 mx-auto">
            <!-- Filters -->
            <div class="gap-2 mb-3 flex flex-wrap items-center">
                <button
                    @click="applyFilter(null)"
                    :class="[
                        'px-3 py-1.5 rounded-md text-xs font-medium whitespace-nowrap transition-colors',
                        !currentFilter
                            ? 'bg-iron-900 text-white'
                            : 'text-iron-500 hover:text-iron-700 hover:bg-iron-100',
                    ]"
                >
                    All
                    <span
                        class="ml-1 tabular-nums"
                        :class="[
                            !currentFilter ? 'text-iron-300' : 'text-iron-400',
                        ]"
                        >{{ totalUsers }}</span
                    >
                </button>
                <button
                    @click="applyFilter('paying')"
                    :class="[
                        'px-3 py-1.5 rounded-md text-xs font-medium whitespace-nowrap transition-colors',
                        currentFilter === 'paying'
                            ? 'bg-iron-900 text-white'
                            : 'text-iron-500 hover:text-iron-700 hover:bg-iron-100',
                    ]"
                >
                    Paying
                </button>
                <button
                    @click="applyFilter('trialing')"
                    :class="[
                        'px-3 py-1.5 rounded-md text-xs font-medium whitespace-nowrap transition-colors',
                        currentFilter === 'trialing'
                            ? 'bg-iron-900 text-white'
                            : 'text-iron-500 hover:text-iron-700 hover:bg-iron-100',
                    ]"
                >
                    Trialing
                </button>
                <button
                    @click="applyFilter('in_paid_team')"
                    :class="[
                        'px-3 py-1.5 rounded-md text-xs font-medium whitespace-nowrap transition-colors',
                        currentFilter === 'in_paid_team'
                            ? 'bg-iron-900 text-white'
                            : 'text-iron-500 hover:text-iron-700 hover:bg-iron-100',
                    ]"
                >
                    In Paid Team
                </button>
                <button
                    @click="applyFilter('affil')"
                    :class="[
                        'px-3 py-1.5 rounded-md text-xs font-medium whitespace-nowrap transition-colors',
                        currentFilter === 'affil'
                            ? 'bg-iron-900 text-white'
                            : 'text-iron-500 hover:text-iron-700 hover:bg-iron-100',
                    ]"
                >
                    Affiliates
                </button>
                <button
                    @click="applyFilter('admin')"
                    :class="[
                        'px-3 py-1.5 rounded-md text-xs font-medium whitespace-nowrap transition-colors',
                        currentFilter === 'admin'
                            ? 'bg-iron-900 text-white'
                            : 'text-iron-500 hover:text-iron-700 hover:bg-iron-100',
                    ]"
                >
                    Admins
                </button>
                <button
                    @click="applyFilter('not_contacted')"
                    :class="[
                        'px-3 py-1.5 rounded-md text-xs font-medium whitespace-nowrap transition-colors',
                        currentFilter === 'not_contacted'
                            ? 'bg-iron-900 text-white'
                            : 'text-iron-500 hover:text-iron-700 hover:bg-iron-100',
                    ]"
                >
                    Not Contacted
                </button>
                <button
                    @click="applyFilter('waiting_response')"
                    :class="[
                        'px-3 py-1.5 rounded-md text-xs font-medium whitespace-nowrap transition-colors',
                        currentFilter === 'waiting_response'
                            ? 'bg-iron-900 text-white'
                            : 'text-iron-500 hover:text-iron-700 hover:bg-iron-100',
                    ]"
                >
                    Waiting for Response
                </button>
            </div>

            <!-- Search -->
            <div class="gap-2 mb-4 flex flex-wrap items-center justify-end">
                <div class="w-96 relative">
                    <Input
                        v-model="searchQuery"
                        type="text"
                        placeholder="Search by email or Stripe Customer ID (cus_xxxx)"
                        class="pl-8 text-sm"
                        @keydown.enter="applySearch"
                    />
                    <span class="left-2.5 absolute top-1/2 -translate-y-1/2">
                        <Search class="h-3.5 w-3.5 text-iron-400" />
                    </span>
                </div>

                <button
                    v-if="!invoiceSearchOpen"
                    type="button"
                    class="gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-md text-iron-500 hover:text-iron-900 hover:bg-iron-100 inline-flex items-center whitespace-nowrap transition-colors"
                    title="Look up a user by their invoice number (slow — calls Stripe)"
                    @click="openInvoiceSearch"
                >
                    <FileText class="h-3 w-3" />
                    Find by invoice number
                </button>

                <div
                    v-else
                    class="gap-1.5 pl-2 pr-1 py-1 rounded-md border-iron-200 bg-iron-50/60 inline-flex items-center border"
                >
                    <FileText class="h-3.5 w-3.5 text-iron-400 shrink-0" />
                    <input
                        ref="invoiceInputRef"
                        v-model="invoiceQuery"
                        type="text"
                        placeholder="e.g. 39EAT953-0145"
                        class="p-0 text-iron-900 placeholder:text-iron-400 font-mono w-44 text-xs border-0 bg-transparent focus:ring-0"
                        :disabled="invoiceLookupLoading"
                        @keydown.enter="lookupByInvoice"
                        @keydown.escape="closeInvoiceSearch"
                    />
                    <button
                        type="button"
                        class="px-2 py-1 font-medium rounded-md bg-iron-900 text-white hover:bg-iron-800 text-[11px] whitespace-nowrap transition-colors"
                        :disabled="invoiceLookupLoading"
                        @click="lookupByInvoice"
                    >
                        {{ invoiceLookupLoading ? "..." : "Find" }}
                    </button>
                    <button
                        type="button"
                        class="p-1 text-iron-400 hover:text-iron-700 hover:bg-iron-100 rounded-md transition-colors"
                        title="Close"
                        @click="closeInvoiceSearch"
                    >
                        <X class="h-3 w-3" />
                    </button>
                </div>
            </div>

            <div
                v-if="invoiceLookupError"
                class="mb-3 px-3 py-2 rounded-md bg-red-50 text-red-700 text-xs"
            >
                {{ invoiceLookupError }}
            </div>

            <!-- Results count -->
            <div class="mb-2 flex items-center justify-between">
                <p
                    class="font-medium text-iron-400 tracking-wider text-[11px] uppercase tabular-nums"
                >
                    {{ users.total }} users
                </p>
                <p
                    v-if="users.last_page > 1"
                    class="font-medium text-iron-400 tracking-wider text-[11px] tabular-nums"
                >
                    Page {{ users.current_page }} of {{ users.last_page }}
                </p>
            </div>

            <!-- Users Table -->
            <div
                class="bg-white rounded-xl border-iron-200 overflow-hidden border"
            >
                <div class="overflow-x-auto">
                    <table class="w-full">
                        <thead>
                            <tr class="border-iron-100 border-b">
                                <th
                                    class="px-5 py-2.5 font-medium text-iron-400 tracking-wider text-left text-[11px] uppercase"
                                >
                                    Name
                                </th>
                                <th
                                    class="px-5 py-2.5 font-medium text-iron-400 tracking-wider text-left text-[11px] uppercase"
                                >
                                    Email
                                </th>
                                <th
                                    class="px-5 py-2.5 font-medium text-iron-400 tracking-wider text-center text-[11px] uppercase"
                                >
                                    Type
                                </th>
                                <th
                                    class="px-5 py-2.5 font-medium text-iron-400 tracking-wider text-center text-[11px] uppercase"
                                >
                                    Plan
                                </th>
                                <th
                                    v-if="isAffilFilter"
                                    class="px-5 py-2.5 font-medium text-iron-400 tracking-wider text-right text-[11px] uppercase"
                                >
                                    To Payout
                                </th>
                                <th
                                    class="px-5 py-2.5 font-medium text-iron-400 tracking-wider text-right text-[11px] uppercase"
                                ></th>
                            </tr>
                        </thead>
                        <tbody class="divide-iron-50 divide-y">
                            <tr
                                v-for="user in users.data"
                                :key="user.id"
                                class="hover:bg-iron-50/70 transition-colors"
                            >
                                <td
                                    class="px-5 py-2.5 text-sm font-medium text-iron-900 whitespace-nowrap"
                                >
                                    {{ user.name }}
                                </td>
                                <td
                                    class="px-5 py-2.5 text-iron-400 font-mono text-[13px] whitespace-nowrap"
                                >
                                    {{ user.email }}
                                </td>
                                <td
                                    class="px-5 py-2.5 text-center whitespace-nowrap"
                                >
                                    <button
                                        @click="
                                            user.affiliatePartner
                                                ? applyFilter('affil')
                                                : applyFilter(user.type)
                                        "
                                    >
                                        <span
                                            :class="typeClass(user)"
                                            class="font-semibold px-2 py-0.5 inline-block rounded-full text-[11px] uppercase"
                                        >
                                            {{
                                                user.affiliatePartner
                                                    ? "Affiliate"
                                                    : user.type
                                            }}
                                        </span>
                                    </button>
                                </td>
                                <td
                                    class="px-5 py-2.5 text-center whitespace-nowrap"
                                >
                                    <button
                                        @click="applyFilter('paying')"
                                        v-if="user.isPayingCustomer"
                                    >
                                        <span
                                            class="font-semibold px-2 py-0.5 bg-emerald-50 text-emerald-600 inline-block rounded-full text-[11px]"
                                        >
                                            Paying
                                        </span>
                                    </button>
                                    <button
                                        @click="applyFilter('trialing')"
                                        v-else-if="user.isTrialing"
                                    >
                                        <span
                                            class="font-semibold px-2 py-0.5 bg-violet-50 text-violet-600 inline-block rounded-full text-[11px]"
                                        >
                                            Trialing
                                        </span>
                                    </button>
                                    <button
                                        @click="applyFilter('in_paid_team')"
                                        v-else-if="user.inPaidTeam"
                                    >
                                        <span
                                            class="font-semibold px-2 py-0.5 bg-blue-50 text-blue-600 inline-block rounded-full text-[11px]"
                                        >
                                            In Paid Team
                                        </span>
                                    </button>
                                    <span
                                        v-else
                                        class="font-semibold px-2 py-0.5 bg-iron-100 text-iron-500 inline-block rounded-full text-[11px]"
                                    >
                                        Free
                                    </span>
                                </td>
                                <td
                                    v-if="isAffilFilter"
                                    class="px-5 py-2.5 text-right whitespace-nowrap"
                                >
                                    <div
                                        class="text-xs font-medium tabular-nums"
                                    >
                                        <span
                                            v-if="
                                                user.toPayoutCzk ||
                                                user.toPayoutEur ||
                                                user.toPayoutUsd
                                            "
                                            class="gap-2 flex items-center justify-end"
                                        >
                                            <span
                                                v-if="user.toPayoutCzk"
                                                class="text-iron-700"
                                                >{{
                                                    user.toPayoutCzk
                                                }}
                                                CZK</span
                                            >
                                            <span
                                                v-if="user.toPayoutEur"
                                                class="text-iron-700"
                                                >{{
                                                    user.toPayoutEur
                                                }}
                                                EUR</span
                                            >
                                            <span
                                                v-if="user.toPayoutUsd"
                                                class="text-iron-700"
                                                >{{
                                                    user.toPayoutUsd
                                                }}
                                                USD</span
                                            >
                                        </span>
                                        <span v-else class="text-iron-300"
                                            >—</span
                                        >
                                    </div>
                                </td>
                                <td
                                    class="px-5 py-2.5 text-right whitespace-nowrap"
                                >
                                    <div
                                        class="gap-2 flex items-center justify-end"
                                    >
                                        <inertia-link
                                            v-if="
                                                user.id !== $page.props.userId
                                            "
                                            :href="
                                                route('app.kiosk.users.show', {
                                                    user: user.id,
                                                })
                                            "
                                            class="gap-1 text-xs font-medium text-iron-500 hover:text-iron-900 inline-flex items-center transition-colors"
                                        >
                                            View
                                        </inertia-link>
                                        <inertia-link
                                            v-if="
                                                user.id !== $page.props.userId
                                            "
                                            :href="
                                                route(
                                                    'app.kiosk.users.impersonate',
                                                    { user: user.id },
                                                )
                                            "
                                            class="gap-1 text-xs font-medium text-iron-500 hover:text-iron-900 inline-flex items-center transition-colors"
                                        >
                                            <UserCog class="h-3 w-3" />
                                            Impersonate
                                        </inertia-link>
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <!-- Empty state -->
                <div v-if="users.data.length === 0" class="py-16 text-center">
                    <div
                        class="w-10 h-10 rounded-lg bg-iron-100 mb-3 mx-auto flex items-center justify-center"
                    >
                        <Users class="h-4 w-4 text-iron-400" />
                    </div>
                    <p class="text-sm text-iron-500">
                        No users match your filters.
                    </p>
                </div>

                <!-- Pagination -->
                <div
                    v-if="users.last_page > 1"
                    class="border-iron-100 px-5 py-3 flex items-center justify-between border-t"
                >
                    <span class="text-iron-400 text-[11px] tabular-nums">
                        Page {{ users.current_page }} of
                        {{ users.last_page }}
                        <span class="text-iron-300 ml-1"
                            >({{ users.total }} total)</span
                        >
                    </span>
                    <div class="gap-1.5 flex">
                        <inertia-link
                            v-if="users.prev_page_url"
                            :href="users.prev_page_url"
                            preserve-state
                            class="rounded-md border-iron-200 px-2.5 py-1 font-medium text-iron-500 hover:bg-iron-50 border text-[11px] transition-colors"
                        >
                            Previous
                        </inertia-link>
                        <inertia-link
                            v-if="users.next_page_url"
                            :href="users.next_page_url"
                            preserve-state
                            class="rounded-md border-iron-200 px-2.5 py-1 font-medium text-iron-500 hover:bg-iron-50 border text-[11px] transition-colors"
                        >
                            Next
                        </inertia-link>
                    </div>
                </div>
            </div>

            <!-- Users with Reservations (Last 60 Days) -->
            <div class="mt-10">
                <div class="gap-3 mb-4 flex items-center">
                    <h2 class="text-lg font-semibold text-iron-900">
                        Active Users
                    </h2>
                    <span
                        class="px-2.5 py-0.5 text-xs font-medium bg-iron-100 text-iron-600 rounded-full"
                    >
                        Last 60 Days
                    </span>
                </div>

                <Deferred data="usersWithReservationsLast60Days">
                    <template #fallback>
                        <div
                            class="bg-white rounded-xl border-iron-200 overflow-hidden border"
                        >
                            <div class="p-6">
                                <div class="space-y-3">
                                    <div
                                        v-for="i in 5"
                                        :key="i"
                                        class="h-10 bg-iron-100 rounded animate-pulse"
                                    ></div>
                                </div>
                            </div>
                        </div>
                    </template>

                    <div
                        class="bg-white rounded-xl border-iron-200 overflow-hidden border"
                    >
                        <div class="overflow-x-auto">
                            <table class="w-full">
                                <thead>
                                    <tr class="border-iron-100 border-b">
                                        <th
                                            class="px-5 py-2.5 font-medium text-iron-400 tracking-wider hover:text-iron-600 cursor-pointer text-left text-[11px] uppercase"
                                            @click="
                                                sortReservationUsers('name')
                                            "
                                        >
                                            <div
                                                class="gap-1 flex items-center"
                                            >
                                                Name
                                                <span
                                                    v-if="
                                                        reservationSortField ===
                                                        'name'
                                                    "
                                                    class="text-iron-500"
                                                >
                                                    {{
                                                        reservationSortDirection ===
                                                        "asc"
                                                            ? "↑"
                                                            : "↓"
                                                    }}
                                                </span>
                                            </div>
                                        </th>
                                        <th
                                            class="px-5 py-2.5 font-medium text-iron-400 tracking-wider text-left text-[11px] uppercase"
                                        >
                                            Email
                                        </th>
                                        <th
                                            class="px-5 py-2.5 font-medium text-iron-400 tracking-wider text-center text-[11px] uppercase"
                                        >
                                            Plan
                                        </th>
                                        <th
                                            class="px-5 py-2.5 font-medium text-iron-400 tracking-wider hover:text-iron-600 cursor-pointer text-right text-[11px] uppercase"
                                            @click="
                                                sortReservationUsers(
                                                    'reservations_count',
                                                )
                                            "
                                        >
                                            <div
                                                class="gap-1 flex items-center justify-end"
                                            >
                                                Reservations
                                                <span
                                                    v-if="
                                                        reservationSortField ===
                                                        'reservations_count'
                                                    "
                                                    class="text-iron-500"
                                                >
                                                    {{
                                                        reservationSortDirection ===
                                                        "asc"
                                                            ? "↑"
                                                            : "↓"
                                                    }}
                                                </span>
                                            </div>
                                        </th>
                                        <th
                                            class="px-5 py-2.5 font-medium text-iron-400 tracking-wider text-left text-[11px] uppercase"
                                        >
                                            Latest
                                        </th>
                                        <th
                                            class="px-5 py-2.5 font-medium text-iron-400 tracking-wider text-right text-[11px] uppercase"
                                        ></th>
                                    </tr>
                                </thead>
                                <tbody class="divide-iron-50 divide-y">
                                    <tr
                                        v-for="user in sortedReservationUsers"
                                        :key="user.id"
                                        class="hover:bg-iron-50/70 transition-colors"
                                    >
                                        <td
                                            class="px-5 py-2.5 text-sm font-medium text-iron-900 whitespace-nowrap"
                                        >
                                            {{ user.name }}
                                        </td>
                                        <td
                                            class="px-5 py-2.5 text-iron-400 font-mono text-[13px] whitespace-nowrap"
                                        >
                                            {{ user.email }}
                                        </td>
                                        <td
                                            class="px-5 py-2.5 text-center whitespace-nowrap"
                                        >
                                            <span
                                                :class="[
                                                    'font-semibold px-2 py-0.5 inline-block rounded-full text-[11px]',
                                                    user.is_paying_customer
                                                        ? 'bg-emerald-50 text-emerald-600'
                                                        : 'bg-iron-100 text-iron-500',
                                                ]"
                                            >
                                                {{
                                                    user.is_paying_customer
                                                        ? "Paying"
                                                        : "Free"
                                                }}
                                            </span>
                                        </td>
                                        <td
                                            class="px-5 py-2.5 text-sm font-semibold text-iron-700 text-right whitespace-nowrap tabular-nums"
                                        >
                                            {{ user.reservations_count }}
                                        </td>
                                        <td
                                            class="px-5 py-2.5 whitespace-nowrap"
                                        >
                                            <div
                                                v-if="
                                                    user.latest_reservation_start
                                                "
                                            >
                                                <div
                                                    class="text-iron-400 text-[11px]"
                                                >
                                                    Created:
                                                    {{
                                                        formatDate(
                                                            user.latest_reservation_date,
                                                        )
                                                    }}
                                                </div>
                                                <div
                                                    class="text-sm text-iron-600 font-mono tabular-nums"
                                                >
                                                    {{
                                                        formatDate(
                                                            user.latest_reservation_start,
                                                        )
                                                    }}
                                                </div>
                                            </div>
                                            <span v-else class="text-iron-300"
                                                >—</span
                                            >
                                        </td>
                                        <td
                                            class="px-5 py-2.5 text-right whitespace-nowrap"
                                        >
                                            <inertia-link
                                                :href="
                                                    route(
                                                        'app.kiosk.users.show',
                                                        {
                                                            user: user.id,
                                                        },
                                                    )
                                                "
                                                class="text-xs font-medium text-iron-500 hover:text-iron-900 transition-colors"
                                            >
                                                View
                                            </inertia-link>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        <div
                            v-if="sortedReservationUsers.length === 0"
                            class="py-16 text-center"
                        >
                            <div
                                class="w-10 h-10 rounded-lg bg-iron-100 mb-3 mx-auto flex items-center justify-center"
                            >
                                <Users class="h-4 w-4 text-iron-400" />
                            </div>
                            <p class="text-sm text-iron-500">
                                No users with reservations in the last 60 days.
                            </p>
                        </div>
                    </div>
                </Deferred>
            </div>
        </div>
    </app-layout>
</template>

<script setup lang="ts">
import AppLayout from "../../../../Layouts/AppLayout.vue";
import KioskNav from "../components/KioskNav.vue";
import route from "ziggy-js";
import { Input } from "../../../../components/ui/input";
import { ref, computed, nextTick } from "vue";
import { Search, Plus, UserCog, Users, FileText, X } from "lucide-vue-next";
import { Deferred, router } from "@inertiajs/vue3";

type KioskUser = {
    id: number;
    name: string;
    email: string;
    type: string;
    affiliatePartner: boolean;
    isPayingCustomer: boolean;
    isTrialing: boolean;
    inPaidTeam: boolean;
    toPayoutCzk: number | null;
    toPayoutEur: number | null;
    toPayoutUsd: number | null;
};

type PaginatedUsers = {
    data: KioskUser[];
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
    next_page_url: string | null;
    prev_page_url: string | null;
};

type UserWithReservations = {
    id: number;
    name: string;
    email: string;
    email_verified_at: string | null;
    is_paying_customer: boolean;
    reservations_count: number;
    latest_reservation_date: string | null;
    latest_reservation_start: string | null;
};

const props = defineProps<{
    users: PaginatedUsers;
    totalUsers: number;
    filter: string | null;
    search: string | null;
    usersWithReservationsLast60Days: UserWithReservations[];
}>();

const currentFilter = computed(() => props.filter);
const isAffilFilter = computed(() => props.filter === "affil");
const searchQuery = ref(props.search ?? "");
const invoiceQuery = ref("");
const invoiceLookupLoading = ref(false);
const invoiceLookupError = ref<string | null>(null);
const invoiceSearchOpen = ref(false);
const invoiceInputRef = ref<HTMLInputElement | null>(null);

async function openInvoiceSearch() {
    invoiceSearchOpen.value = true;
    invoiceLookupError.value = null;
    await nextTick();
    invoiceInputRef.value?.focus();
}

function closeInvoiceSearch() {
    invoiceSearchOpen.value = false;
    invoiceQuery.value = "";
    invoiceLookupError.value = null;
}

function lookupByInvoice() {
    const number = invoiceQuery.value.trim();
    if (number === "" || invoiceLookupLoading.value) {
        return;
    }

    router.post(
        route("app.kiosk.users.invoice-lookups.store"),
        { invoice_number: number },
        {
            preserveScroll: true,
            onStart: () => {
                invoiceLookupLoading.value = true;
                invoiceLookupError.value = null;
            },
            onError: (errors) => {
                invoiceLookupError.value =
                    errors.invoice_number ?? "Lookup failed. Please try again.";
            },
            onFinish: () => {
                invoiceLookupLoading.value = false;
            },
        },
    );
}

const reservationSortField = ref<string>("reservations_count");
const reservationSortDirection = ref<"asc" | "desc">("desc");

function formatDate(dateString: string | null): string {
    if (!dateString) return "—";
    try {
        const d = new Date(dateString);
        if (isNaN(d.getTime())) return dateString;
        return d.toLocaleString("en-GB", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch {
        return dateString;
    }
}

const sortedReservationUsers = computed(() => {
    const users = [...props.usersWithReservationsLast60Days];
    return users.sort((a, b) => {
        const aValue =
            a[reservationSortField.value as keyof UserWithReservations];
        const bValue =
            b[reservationSortField.value as keyof UserWithReservations];

        let comparison = 0;
        if (typeof aValue === "string" && typeof bValue === "string") {
            comparison = aValue.localeCompare(bValue);
        } else if (typeof aValue === "number" && typeof bValue === "number") {
            comparison = aValue - bValue;
        }

        return reservationSortDirection.value === "asc"
            ? comparison
            : -comparison;
    });
});

function sortReservationUsers(field: string) {
    if (reservationSortField.value === field) {
        reservationSortDirection.value =
            reservationSortDirection.value === "asc" ? "desc" : "asc";
    } else {
        reservationSortField.value = field;
        reservationSortDirection.value =
            field === "reservations_count" ? "desc" : "asc";
    }
}

function applyFilter(filter: string | null) {
    const params: Record<string, string> = {};
    if (filter) {
        params.filter = filter;
    }
    if (searchQuery.value) {
        params.search = searchQuery.value;
    }
    router.get(route("app.kiosk.users.index"), params, {
        preserveState: true,
    });
}

function applySearch() {
    const params: Record<string, string> = {};
    if (currentFilter.value) {
        params.filter = currentFilter.value;
    }
    if (searchQuery.value) {
        params.search = searchQuery.value;
    }
    router.get(route("app.kiosk.users.index"), params, {
        preserveState: true,
    });
}

function typeClass(user: KioskUser): string {
    if (user.affiliatePartner) return "bg-amber-50 text-amber-600";
    const map: Record<string, string> = {
        admin: "bg-blue-50 text-blue-600",
        regular: "bg-iron-100 text-iron-500",
    };
    return map[user.type] ?? "bg-iron-100 text-iron-500";
}
</script>
